import React, { Suspense, lazy, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import HUDNav from '@/components/layout/HUDNav';
import CursorTrail from '@/components/effects/CursorTrail';
import { SOCIAL_LINKS, BRAND } from '@/config/site';
import { ROOM_REGISTRY } from '@/config/navConfig';
import { Instagram, Linkedin, Youtube, Music2, X, Facebook } from 'lucide-react';
import Logo from '@/components/branding/Logo';
import { useSiteCopy } from '@/context/SiteCopyContext';
const FirstVisitLanding = lazy(() => import('@/components/landing/GalaxyLanding').then((m) => ({ default: m.FirstVisitLanding })));
import { GuideDownload } from '@/components/GuideDownload';
import AssistantBubble from '@/components/AssistantBubble';
import { captureReferral } from '@/lib/analytics';
import { SignalShell, isSignalPath } from '@/signal/SignalShell';
import { ShopNavigation } from '@/components/shop/ShopNavigation';
import '@/components/shop/shop.css';
import '@/signal/facility-home.css';
import { PublicContacts } from '@/components/PublicContacts';
import { SectionNav, isSectionPath } from '@/signal/SectionNav';

const NeuralCanvas = lazy(() => import('@/components/effects/NeuralCanvas'));

// Routes that keep the original HUD shell (immersive or signed-in app areas).
const LEGACY_SHELL_PREFIXES = ['/admin', '/welcome', '/desk', '/play/field-office', '/labs/server-farm/build'];

// Mirrors FirstVisitLanding's own guard so the three.js chunk (~755KB) is only
// downloaded when the overlay will actually show (first visit, root path).
function LandingGate() {
    const [load, setLoad] = useState(false);
    useEffect(() => {
        try {
            const seen = localStorage.getItem('smi_landing_seen') === '1';
            const path = window.location.pathname;
            if (!seen && (path === '/' || path === '')) setLoad(true);
        } catch {
            setLoad(true);
        }
    }, []);
    if (!load) return null;
    return (
        <Suspense fallback={null}>
            <FirstVisitLanding />
        </Suspense>
    );
}

function SiteBanner({ inline = false }) {
    const enabled = useSiteCopy('banner.enabled');
    const text = useSiteCopy('banner.text');
    const href = useSiteCopy('banner.href');
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        try { setDismissed(sessionStorage.getItem('smi_banner_dismissed') === '1'); } catch {}
    }, []);
    if (!enabled || !text || dismissed) return null;
    const dismiss = () => {
        setDismissed(true);
        try { sessionStorage.setItem('smi_banner_dismissed', '1'); } catch {}
        window.dispatchEvent(new CustomEvent('smi:banner-dismissed'));
    };
    const Inner = (
        <span data-testid="site-banner-text" className="text-white text-xs sm:text-sm">{text}</span>
    );
    return (
        <div
            data-testid="site-banner"
            className={inline ? 'relative border-b border-white/10 bg-[#112337]' : 'fixed top-0 left-0 right-0 z-[60] border-b border-white/10 bg-gradient-to-r from-lab-blue/25 via-lab-violet/25 to-lab-pink/25 backdrop-blur-xl'}
            style={inline ? undefined : { height: 'var(--smi-banner-h, 40px)' }}
        >
            <div className={`mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between gap-3 ${inline ? 'py-1' : 'h-full'}`}>
                {href ? <a href={href} data-testid="site-banner-link" className={inline ? 'min-w-0 min-h-[44px] flex items-center py-2 hover:underline' : 'hover:underline'}>{Inner}</a> : Inner}
                <button onClick={dismiss} data-testid="site-banner-dismiss" aria-label="Dismiss announcement" className={inline ? 'text-white/80 hover:text-white w-11 h-11 shrink-0 flex items-center justify-center' : 'text-white/80 hover:text-white p-1 -mr-1'}>
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

// Ambient three.js background (~760KB chunk): skipped for reduced-motion/Save-Data
// visitors, otherwise deferred to idle (2s timeout fallback) so it never competes
// with page content. Appearance when loaded is unchanged.
function AmbientBackground() {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        try {
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
            if (navigator.connection?.saveData) return;
        } catch { /* ignore */ }
        let idleId, timerId;
        const go = () => setReady(true);
        if ('requestIdleCallback' in window) {
            idleId = window.requestIdleCallback(go, { timeout: 2000 });
        } else {
            timerId = window.setTimeout(go, 2000);
        }
        return () => {
            if (idleId) window.cancelIdleCallback(idleId);
            if (timerId) window.clearTimeout(timerId);
        };
    }, []);
    if (!ready) return null;
    return (
        <Suspense fallback={null}>
            <NeuralCanvas />
        </Suspense>
    );
}

export default function Layout({ children }) {
    const location = useLocation();
    useEffect(() => { captureReferral(); }, []);

    if (location.pathname === '/') return (
        <div className="facility-home" data-testid="facility-home-layout">
            <div className="facility-home-backdrop" data-testid="facility-home-static-background" aria-hidden="true" />
            <AmbientBackground />
            <SignalShell announcement={<SiteBanner inline/>}>{children}<AssistantBubble /></SignalShell>
        </div>
    );
    const automationPage = isSignalPath(location.pathname);
    const shopPage = automationPage || location.pathname.startsWith('/shop') || location.pathname === '/checkout';
    const storePage = location.pathname.startsWith('/shop') || location.pathname === '/checkout';

    // Approved 2026-09-30: one business header/footer (SignalShell) on every public page.
    // Full-screen facility/app areas keep the original HUD shell.
    const legacyShell = LEGACY_SHELL_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    if (!legacyShell) return (
        <div className={`App min-h-screen relative ${shopPage ? 'shop-surface' : ''}`} data-testid="unified-layout">
            <AmbientBackground />
            <SignalShell announcement={<SiteBanner inline/>} wrap={automationPage}>
                {automationPage ? <div className="shop-surface">{children}</div> : (
                    <div className="signal-legacy-main" data-testid="legacy-page-content">
                        {isSectionPath(location.pathname) && <SectionNav/>}
                        {storePage && <ShopNavigation/>}
                        {children}
                    </div>
                )}
                <AssistantBubble />
            </SignalShell>
        </div>
    );

    return (
        <div className={`App min-h-screen relative ${shopPage ? 'shop-surface' : ''}`}>
            <AmbientBackground />
            <CursorTrail />

            <SiteBanner />
            <HUDNav />

            <AnimatePresence mode="wait">
                <motion.main
                    key={location.pathname}
                    initial={{ opacity: 0, y: 12, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -8, filter: 'blur(6px)' }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative z-10 pt-24"
                >
                    {shopPage && <ShopNavigation/>}
                    {automationPage ? <div className="signal shop-surface">{children}</div> : children}
                </motion.main>
            </AnimatePresence>

            <SiteFooter />
            <AssistantBubble />
            <LandingGate />
        </div>
    );
}

const SOCIAL_ICON = {
    instagram: Instagram,
    tiktok: Music2, // lucide has no TikTok — Music2 is a close semantic fit for now
    linkedin: Linkedin,
    facebook: Facebook,
    youtube: Youtube,
};

function SiteFooter() {
    return (
        <footer className="relative z-10 border-t border-white/5 mt-24" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
                <div className="md:col-span-2">
                    <div className="lab-overline mb-3">Signal.Origin</div>
                    <div className="flex items-center gap-3">
                        <Logo size={40} />
                        <div className="font-display text-2xl text-white leading-tight tracking-[0.08em]">
                            S.M.I.
                        </div>
                    </div>
                    <div className="lab-mono text-[10px] text-lab-mute tracking-[0.2em] mt-2">
                        SYNTHETIC MEDIA INVESTIGATOR
                    </div>
                    <p className="text-lab-mute text-sm mt-4 max-w-sm">
                        {BRAND.tagline}
                    </p>
                    <p className="text-lab-dim text-xs mt-1 max-w-sm">{BRAND.subline}</p>
                    <p className="lab-mono text-[9px] text-lab-dim mt-3 max-w-sm" data-testid="footer-bay-area">
                        Built for businesses from the San Francisco Bay Area and beyond.
                    </p>
                    <PublicContacts prefix="site-footer"/>

                    <div className="mt-5" data-testid="footer-guide-download">
                        <GuideDownload from="footer" compact />
                    </div>

                    {/* Social */}
                    <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
                        {SOCIAL_LINKS.map((s) => {
                            const Icon = SOCIAL_ICON[s.icon] || Instagram;
                            return (
                                <a
                                    key={s.name}
                                    href={s.href}
                                    target={s.href && s.href !== '#' ? '_blank' : undefined}
                                    rel="noopener noreferrer"
                                    aria-label={s.name}
                                    data-testid={s.testid}
                                    className="w-10 h-10 rounded-xl glass flex items-center justify-center text-white/70 hover:text-white hover:border-lab-blue/40 transition-colors"
                                >
                                    <Icon className="w-4 h-4" />
                                </a>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <div className="lab-overline mb-3">Rooms</div>
                    <ul className="text-sm text-lab-mute space-y-2 font-mono">
                        {ROOM_REGISTRY.map((r) => (
                            <li key={r.code}>
                                <Link
                                    to={r.to}
                                    data-testid={r.code === '09' ? 'footer-labs-link' : `footer-room-${r.code}`}
                                    className="hover:text-white transition-colors"
                                >
                                    {r.code} · {r.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="lab-overline mb-3">Legal & Status</div>
                    <ul className="text-sm text-lab-mute space-y-2">
                        <li>
                            <Link
                                to="/roadmap"
                                data-testid="footer-roadmap-link"
                                className="hover:text-white transition-colors"
                            >
                                The Roadmap
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/privacy"
                                data-testid="footer-privacy-link"
                                className="hover:text-white transition-colors"
                            >
                                Privacy Policy
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/terms"
                                data-testid="footer-terms-link"
                                className="hover:text-white transition-colors"
                            >
                                Terms of Service
                            </Link>
                        </li>
                    </ul>
                    <div className="glass rounded-xl p-4 text-sm mt-5">
                        <div className="pulse-dot text-lab-emerald font-mono text-xs mb-2">
                            LAB ONLINE
                        </div>
                        <div className="text-lab-mute text-xs">
                            Phase 1 · Payments open in Phase 2.
                        </div>
                    </div>
                </div>
            </div>
            <div className="border-t border-white/5">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-lab-dim font-mono">
                    <span>© 2026 SYNTHETIC MEDIA INVESTIGATOR · S.M.I.</span>
                    <span>Verify. Detect. Protect. Empower Creators.</span>
                </div>
            </div>
        </footer>
    );
}
